import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ShoppingBag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { VariantPicker } from '../components/VariantPicker'
import { useProduct } from '../hooks/useProduct'
import { useCart } from '../hooks/useCart'
import { useDocumentMeta } from '@/lib/useDocumentMeta'
import type { PrintifyProductImage, PrintifyProductVariant } from '../types'
import { withErrorCode } from '@/lib/errors'

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)
}

function getImageForVariant(
  images: PrintifyProductImage[],
  variant: PrintifyProductVariant | null
): string {
  if (variant) {
    const variantImg = images.find((img) => img.variant_ids.includes(variant.id))
    if (variantImg) return variantImg.src
  }
  return images.find((i) => i.is_default)?.src ?? images[0]?.src ?? ''
}

export function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>()
  const { product, loading, error } = useProduct(productId)
  const { addItem } = useCart()
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>({})
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [addedFeedback, setAddedFeedback] = useState(false)

  const activeVariant = useMemo<PrintifyProductVariant | null>(() => {
    if (!product) return null
    // Products with no options: auto-select the first enabled variant
    if (product.options.length === 0) {
      return product.variants.find((v) => v.is_enabled) ?? null
    }
    if (Object.keys(selectedOptions).length < product.options.length) return null
    return (
      product.variants.find(
        (v) =>
          v.is_enabled &&
          v.options.every((valueId, idx) => selectedOptions[idx] === valueId)
      ) ?? null
    )
  }, [product, selectedOptions])

  // Filter the gallery to images matching the selected color (when a color
  // option is present and chosen). Falls back to all images otherwise.
  const galleryImages = useMemo(() => {
    if (!product) return []
    const colorIdx = product.options.findIndex((o) =>
      /colou?r/i.test(o.name)
    )
    const selectedColorId =
      colorIdx >= 0 ? selectedOptions[colorIdx] : undefined
    if (selectedColorId === undefined) return product.images

    const colorVariantIds = new Set(
      product.variants
        .filter((v) => v.options[colorIdx] === selectedColorId)
        .map((v) => v.id)
    )
    const filtered = product.images.filter((img) =>
      img.variant_ids.some((id) => colorVariantIds.has(id))
    )
    return filtered.length > 0 ? filtered : product.images
  }, [product, selectedOptions])

  const displayImage = useMemo(() => {
    if (!product) return ''
    if (selectedImage && galleryImages.some((i) => i.src === selectedImage)) {
      return selectedImage
    }
    const variantSrc = getImageForVariant(galleryImages, activeVariant)
    if (variantSrc) return variantSrc
    return galleryImages[0]?.src ?? getImageForVariant(product.images, activeVariant)
  }, [product, activeVariant, selectedImage, galleryImages])

  const metaDescription = useMemo(() => {
    if (!product?.description) return 'Shop One Day Dance merch — limited-run apparel and gear.'
    const stripped = stripHtml(product.description)
    return stripped.length > 160 ? stripped.slice(0, 157) + '…' : stripped
  }, [product])

  useDocumentMeta(product?.title ?? 'Shop', metaDescription)

  function handleOptionChange(optionIndex: number, valueId: number) {
    if (!product) return
    setSelectedOptions((prev) => {
      const next: Record<number, number> = { ...prev, [optionIndex]: valueId }
      // For every other already-selected option, verify a combination exists.
      // If not, snap that option to its closest available value (by index in option.values).
      for (const other of product.options) {
        const otherIdx = product.options.indexOf(other)
        if (otherIdx === optionIndex) continue
        const current = next[otherIdx]
        if (current === undefined) continue
        const combinationExists = product.variants.some(
          (v) =>
            v.is_enabled &&
            Object.entries(next).every(([idxStr, selId]) => v.options[Number(idxStr)] === selId)
        )
        if (combinationExists) continue
        const currentValueIdx = other.values.findIndex((val) => val.id === current)
        const candidates = other.values
          .map((val, idx) => ({ val, idx }))
          .filter(({ val }) =>
            product.variants.some(
              (v) =>
                v.is_enabled &&
                v.options[otherIdx] === val.id &&
                Object.entries(next).every(
                  ([idxStr, selId]) =>
                    Number(idxStr) === otherIdx || v.options[Number(idxStr)] === selId
                )
            )
          )
          .sort((a, b) => Math.abs(a.idx - currentValueIdx) - Math.abs(b.idx - currentValueIdx))
        const best = candidates[0]
        if (best) next[otherIdx] = best.val.id
      }
      return next
    })
    setSelectedImage(null)
  }

  function handleAddToCart() {
    if (!product || !activeVariant) return
    addItem({
      productId: product.id,
      variantId: activeVariant.id,
      productTitle: product.title,
      variantTitle: activeVariant.title,
      price: activeVariant.price,
      quantity: 1,
      imageUrl: getImageForVariant(product.images, activeVariant),
    })
    setAddedFeedback(true)
    setTimeout(() => setAddedFeedback(false), 2000)
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="grid gap-8 md:grid-cols-2">
          <Skeleton className="aspect-square w-full rounded-lg" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-5 w-1/4" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <p className="text-destructive">
          {error ?? withErrorCode('Product not found.', 'shop/product-not-found')}
        </p>
        <Link to="/shop" className="mt-4 inline-block text-sm text-muted-foreground hover:underline">
          ← Back to shop
        </Link>
      </div>
    )
  }

  const allOptionsSelected =
    product.options.length === 0 ||
    Object.keys(selectedOptions).length === product.options.length

  return (
    <div className="container mx-auto px-4 py-12">
      <Link
        to="/shop"
        className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to shop
      </Link>

      <div className="grid gap-8 md:grid-cols-2">
        {/* Images */}
        <div className="space-y-3">
          <div className="aspect-square overflow-hidden rounded-lg bg-muted">
            {displayImage ? (
              <img src={displayImage} alt={product.title} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                No image
              </div>
            )}
          </div>
          {galleryImages.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {galleryImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(img.src)}
                  className={`h-14 w-14 shrink-0 overflow-hidden rounded border-2 transition-colors ${
                    (selectedImage ?? displayImage) === img.src
                      ? 'border-foreground'
                      : 'border-transparent'
                  }`}
                >
                  <img src={img.src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold">{product.title}</h1>
            {activeVariant ? (
              <p className="mt-1 text-xl font-semibold">{formatPrice(activeVariant.price)}</p>
            ) : product.variants.length > 0 ? (
              <p className="mt-1 text-xl font-semibold">
                From{' '}
                {formatPrice(
                  Math.min(...product.variants.filter((v) => v.is_enabled).map((v) => v.price))
                )}
              </p>
            ) : null}
          </div>

          {product.options.length > 0 && (
            <VariantPicker
              options={product.options}
              variants={product.variants}
              selectedOptions={selectedOptions}
              onOptionChange={handleOptionChange}
            />
          )}

          <Button
            className="w-full"
            size="lg"
            disabled={!allOptionsSelected || !activeVariant}
            onClick={handleAddToCart}
          >
            <ShoppingBag className="mr-2 h-4 w-4" />
            {addedFeedback ? 'Added!' : !allOptionsSelected ? 'Select options' : 'Add to Cart'}
          </Button>

          {product.description && (
            <div
              className="prose prose-sm max-w-none text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: product.description }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
